'use client';

import {
  REAUTH_COMPLETE_MESSAGE,
  REAUTH_POLL_INTERVAL_MS,
  REAUTH_POLL_TIMEOUT_MS,
  VERIFY_ATTEMPTS,
  VERIFY_BACKOFF_MS,
} from '@/features/auth/constants';
import { isSessionExpired, subscribeSessionExpired } from '@/features/auth/session-recovery';
import { clearCachedClientToken } from '@/services/api';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@andritzot/metris-web-ui/feedback/dialog';
import { delay } from '@andritzot/metris-web-utils/delay';
import { useQueryClient } from '@tanstack/react-query';
import { getSession, signIn, useSession } from 'next-auth/react';
import { usePathname, useRouter } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';

const POPUP_FEATURES = 'width=520,height=700,menubar=no,toolbar=no,location=no,status=no';
const POPUP_NAME = 'sf-reauth';

const RETAINED_STATE_NOTE = 'Anything you have not saved on this page is kept.';

// The re-authentication popup lives under /auth/reauth and is served by the same layout that mounts
// this dialog.
const AUTH_ROUTE_PREFIX = '/auth/';

/**
 * Recovers a session whose refresh token can no longer be exchanged.
 *
 * A hidden iframe would be quieter than a popup, but Keycloak frames itself only for same-origin
 * parents and browsers refuse to write the SameSite=Lax session cookie on a cross-site subframe
 * navigation, so a silent renew would fail in exactly the situation it exists for.
 */
const SessionExpiredDialog = () => {
  const { data: session, update } = useSession();
  const pathname = usePathname();
  const router = useRouter();
  const queryClient = useQueryClient();
  // A 401 the API client could not recover from arrives through the subscription; a refresh failure
  // recorded in the session arrives through useSession. Either one means the same thing to the user.
  const [isUnauthorized, setIsUnauthorized] = useState(false);
  const [reauthState, setReauthState] = useState<'idle' | 'verifying' | 'failed'>('idle');
  const [isAwaitingPopup, setIsAwaitingPopup] = useState(false);
  const hasSessionError = isSessionExpired(session);
  // Gated on the session itself, not on `status`: the provider reports status 'loading' during every
  // session read while still exposing the previous session, so a status gate blinks the dialog out on
  // each read. A visitor who never signed in has no session, which is what actually has to be
  // excluded here.
  //
  // The popup carries the very session error this dialog reacts to, so without the pathname guard it
  // draws a second copy of the dialog over the sign-in redirect it was opened to perform - and that
  // copy's own Sign in button opens a third popup.
  const isOpen =
    !pathname.startsWith(AUTH_ROUTE_PREFIX) && session?.user != null && (hasSessionError || isUnauthorized);

  useEffect(() => subscribeSessionExpired(() => setIsUnauthorized(true)), []);

  // Held in a ref so the effects below do not depend on `update`'s identity. next-auth memoises the
  // session context on [session, loading], so calling update() flips `loading`, hands out a new
  // update(), and would re-run those effects — whose cleanup cancels the very work that made the call.
  const updateRef = useRef(update);
  useEffect(() => {
    updateRef.current = update;
  }, [update]);

  // Finalizes a recovery WHOEVER delivered it — the completion message, the direct poll, or a
  // broadcast from the popup's own pages closing the dialog without confirmReauth ever running.
  // Keyed on the dialog transitioning open -> closed rather than on the session error, so the
  // 401-triggered path (isUnauthorized, where session.error was never set) finalizes too.
  //
  // Two stale layers have to be repaired, because both were populated while the session was dead:
  // the query cache (the backend user among it) is invalidated, and the server-rendered tree is
  // re-requested with router.refresh() — the header's signed-in state is decided by a SERVER
  // component from the request cookie, so no amount of client-side session repair can update it.
  // router.refresh() re-renders server components while keeping client component state, which is
  // exactly the boundary this dialog promises to protect.
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (isOpen) {
      wasOpenRef.current = true;
      return;
    }
    if (!wasOpenRef.current) return;
    wasOpenRef.current = false;
    clearCachedClientToken();
    setReauthState('idle');
    setIsAwaitingPopup(false);
    void queryClient.invalidateQueries();
    router.refresh();
  }, [isOpen, queryClient, router]);

  // The message from the popup is only the fast path. An identity-provider page that sets
  // Cross-Origin-Opener-Policy severs window.opener for the rest of the popup's life, and a severed
  // opener swallows postMessage with no error anywhere - the sign-in completes, the popup closes,
  // and this tab hears nothing. So while a popup is out, the session is also polled directly; the
  // healthy read lands in the provider via update() and the finalizer above does the rest.
  useEffect(() => {
    if (!isAwaitingPopup) return;

    let isStopped = false;
    const startedAtMs = Date.now();

    const intervalId = setInterval(() => {
      if (Date.now() - startedAtMs > REAUTH_POLL_TIMEOUT_MS) {
        setIsAwaitingPopup(false);
        return;
      }
      void (async () => {
        const refreshed = await getSession({ broadcast: false });
        if (isStopped || refreshed == null || isSessionExpired(refreshed)) return;
        setIsAwaitingPopup(false);
        // The 401-triggered flag is not derived from the session, so the poll has to clear it
        // itself or a dialog opened by that path would never close on a polled recovery.
        setIsUnauthorized(false);
        await updateRef.current();
      })();
    }, REAUTH_POLL_INTERVAL_MS);

    return () => {
      isStopped = true;
      clearInterval(intervalId);
    };
  }, [isAwaitingPopup]);

  // Attached for the component's whole lifetime, not per isOpen: the completion message is the only
  // recovery signal that survives the real flow (the popup closes itself before its last session
  // read can broadcast), and it can arrive while a concurrent session read has the dialog's state in
  // flux. A listener keyed to isOpen was measured losing exactly that message.
  useEffect(() => {
    let isUnmounted = false;
    let isVerifying = false;

    const confirmReauth = async () => {
      // A duplicated completion message would otherwise run a second verify loop interleaved with
      // the first, both writing the same state.
      if (isVerifying) return;
      isVerifying = true;
      clearCachedClientToken();
      setReauthState('verifying');

      try {
        for (let attempt = 0; attempt < VERIFY_ATTEMPTS && !isUnmounted; attempt++) {
          if (attempt > 0) await delay(VERIFY_BACKOFF_MS * attempt);

          // getSession rather than update(), because update() mutates provider state effects here
          // are keyed on, and returns undefined whenever the provider is already mid-read. The
          // recovered session is pushed through update() only once it is confirmed.
          const refreshed = await getSession({ broadcast: false });
          if (refreshed != null && !isSessionExpired(refreshed)) {
            await updateRef.current();
            setIsUnauthorized(false);
            setReauthState('idle');
            return;
          }
        }

        if (!isUnmounted) setReauthState('failed');
      } finally {
        isVerifying = false;
      }
    };

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== globalThis.location.origin) return;
      if (event.data !== REAUTH_COMPLETE_MESSAGE) return;
      void confirmReauth();
    };

    globalThis.addEventListener('message', onMessage);
    return () => {
      isUnmounted = true;
      globalThis.removeEventListener('message', onMessage);
    };
  }, []);

  const handleSignIn = () => {
    setReauthState('idle');
    const popup = globalThis.open('/auth/reauth', POPUP_NAME, POPUP_FEATURES);
    // Blocked despite the click gesture. A full-page sign-in still recovers the session, at the cost
    // of the page state this dialog exists to protect.
    if (popup == null) {
      void signIn('keycloak');
      return;
    }
    setIsAwaitingPopup(true);
  };

  if (!isOpen) return null;

  return (
    <Dialog open size="xs" fixedHeight={false} disableEscapeKeyDown>
      <DialogTitle title="Your session has expired" />
      <DialogContent>
        {reauthState === 'failed'
          ? 'Signing in did not restore the session. Try again. '
          : 'Sign in again to continue. '}
        {RETAINED_STATE_NOTE}
      </DialogContent>
      <DialogActions
        primaryAction={{ label: 'Sign in', onClick: handleSignIn, loading: reauthState === 'verifying' }}
      />
    </Dialog>
  );
};

export default SessionExpiredDialog;
